/**
 * MermaidBlock — Dedicated custom block for mermaid diagrams.
 * Replaces the fragile MutationObserver approach with a first-class block type.
 *
 * Features:
 * - Source/Diagram toggle view
 * - Lazy rendering via IntersectionObserver (only renders when near viewport)
 * - Debounced re-render on source change (500ms)
 * - Zoom, fullscreen, download SVG toolbar
 * - Zero memory leaks (no createRoot per diagram)
 */

import { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { Code2, Image, ZoomIn, ZoomOut, Download, Maximize2, X } from 'lucide-react';

// Lazy-load MermaidDiagram to avoid loading mermaid.js until needed
const MermaidDiagram = lazy(() =>
  import('../../documentation/MermaidDiagram').then(m => ({ default: m.MermaidDiagram }))
);

interface MermaidBlockRendererProps {
  block: {
    id: string;
    props: { code: string };
  };
  editor: {
    updateBlock: (id: string, update: { props: Partial<{ code: string }> }) => void;
    isEditable: boolean;
  };
}

function MermaidBlockRenderer({ block, editor }: MermaidBlockRendererProps) {
  const { code } = block.props;
  const [view, setView] = useState<'source' | 'diagram'>('diagram');
  const [localCode, setLocalCode] = useState(code);
  const [isVisible, setIsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const isEditable = editor.isEditable;

  // Download rendered SVG
  const handleDownloadSVG = useCallback(() => {
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mermaid-${block.id.slice(0, 8)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [block.id]);

  // Sync when prop changes externally (e.g. undo/redo)
  useEffect(() => {
    setLocalCode(code);
  }, [code]);

  // IntersectionObserver: only render diagram when within 200px of viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Debounced update to editor block props
  const handleCodeChange = useCallback((newCode: string) => {
    setLocalCode(newCode);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      editor.updateBlock(block.id, { props: { code: newCode } });
    }, 500);
  }, [editor, block.id]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  return (
    <div
      ref={containerRef}
      className="mermaid-block group relative rounded-xl border border-stone-200/80 bg-white overflow-hidden my-3 shadow-sm hover:shadow-md transition-shadow duration-200"
      contentEditable={false}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-b from-stone-50 to-stone-100/50 border-b border-stone-200/60 text-xs">
        <div className="flex items-center gap-0.5 bg-white/80 rounded-lg p-0.5 shadow-inner">
          <button
            onClick={() => setView('source')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-150 ${
              view === 'source'
                ? 'bg-white shadow-sm text-stone-800 font-medium'
                : 'text-stone-500 hover:text-stone-700 hover:bg-white/60'
            }`}
          >
            <Code2 size={12} /> Source
          </button>
          <button
            onClick={() => setView('diagram')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-150 ${
              view === 'diagram'
                ? 'bg-white shadow-sm text-stone-800 font-medium'
                : 'text-stone-500 hover:text-stone-700 hover:bg-white/60'
            }`}
          >
            <Image size={12} /> Diagram
          </button>
        </div>
        <div className="flex items-center gap-1">
          {view === 'diagram' && (
            <>
              <button
                onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                className="p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-white/60 transition-colors"
                title="Zoom out"
              >
                <ZoomOut size={12} />
              </button>
              <span className="text-stone-400 w-8 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom(z => Math.min(2, z + 0.25))}
                className="p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-white/60 transition-colors"
                title="Zoom in"
              >
                <ZoomIn size={12} />
              </button>
              <div className="w-px h-3.5 bg-stone-200 mx-0.5" />
              <button
                onClick={handleDownloadSVG}
                className="p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-white/60 transition-colors"
                title="Download SVG"
              >
                <Download size={12} />
              </button>
              <button
                onClick={() => setIsFullscreen(f => !f)}
                className="p-1 rounded-md text-stone-400 hover:text-stone-600 hover:bg-white/60 transition-colors"
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <X size={12} /> : <Maximize2 size={12} />}
              </button>
            </>
          )}
          <span className="text-stone-300 font-medium ml-1.5 tracking-wide uppercase text-[10px]">Mermaid</span>
        </div>
      </div>

      {/* Source view */}
      {view === 'source' && (
        <div className="p-3">
          <textarea
            value={localCode}
            onChange={(e) => handleCodeChange(e.target.value)}
            readOnly={!isEditable}
            className="w-full min-h-[120px] p-3 font-mono text-sm bg-stone-900 text-stone-100 rounded-lg border-0 resize-y focus:outline-none focus:ring-1 focus:ring-coral-500"
            placeholder="graph LR&#10;  A --> B"
            spellCheck={false}
          />
        </div>
      )}

      {/* Diagram view */}
      {view === 'diagram' && (
        <div className={`p-4 min-h-[80px] overflow-auto ${isFullscreen ? 'fixed inset-0 z-[60] bg-white flex items-center justify-center' : ''}`}>
          {isFullscreen && (
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 p-2 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors z-10"
            >
              <X size={16} />
            </button>
          )}
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
            {isVisible && localCode.trim() ? (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center py-8 text-stone-400 text-sm">
                    Loading mermaid...
                  </div>
                }
              >
                <MermaidDiagram chart={localCode} />
              </Suspense>
            ) : !localCode.trim() ? (
              <div className="text-center py-6 text-stone-400 text-sm">
                No mermaid code. Switch to Source view to add diagram code.
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-stone-400 text-sm">
                Scroll into view to render...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Mermaid diagram block spec for BlockNote.
 */
export const mermaidBlockSpec = createReactBlockSpec(
  {
    type: 'mermaidDiagram' as const,
    propSchema: {
      code: { default: 'graph LR\n  A --> B' },
    },
    content: 'none',
  },
  {
    render: (props) => {
      const blockProps: MermaidBlockRendererProps = {
        block: {
          id: props.block.id,
          props: props.block.props as { code: string },
        },
        editor: {
          updateBlock: (id, update) => (props.editor as any).updateBlock(id, update),
          isEditable: (props.editor as any).isEditable,
        },
      };
      return <MermaidBlockRenderer {...blockProps} />;
    },
  }
);

export default mermaidBlockSpec;
